import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Plus, Search, SlidersHorizontal, ArrowUpDown } from "lucide-react";
import { toast } from "sonner";
import { MobileRecordCard } from "./MobileRecordCard";
import { RecordEditor } from "@/components/grid/RecordEditor";
import { FilterBuilder } from "@/components/grid/FilterBuilder";
import { SortBuilder } from "@/components/grid/SortBuilder";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";

interface MobileGridViewProps {
  table: any;
  workspaceId?: string;
}

export const MobileGridView = ({ table, workspaceId }: MobileGridViewProps) => {
  const { user } = useAuth();
  const [fields, setFields] = useState<any[]>([]);
  const [records, setRecords] = useState<any[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [editingRecord, setEditingRecord] = useState(false);
  const [filters, setFilters] = useState<any[]>([]);
  const [sorts, setSorts] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    if (table) {
      fetchData();
    }
  }, [table]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [records, filters, sorts, searchQuery]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [fieldsResult, recordsResult] = await Promise.all([
        supabase
          .from("fields")
          .select("*")
          .eq("table_id", table.id)
          .order("order_index"),
        supabase
          .from("records")
          .select("*")
          .eq("table_id", table.id)
          .order("created_at", { ascending: false }),
      ]);

      if (fieldsResult.error) throw fieldsResult.error;
      if (recordsResult.error) throw recordsResult.error;

      setFields(fieldsResult.data || []);
      setRecords(recordsResult.data || []);
    } catch (error: any) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = [...records];

    if (searchQuery) {
      filtered = filtered.filter((record) =>
        Object.values(record.data || {}).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    filters.forEach((filter) => {
      filtered = filtered.filter((record) => {
        const value = record.data?.[filter.field];
        const filterValue = filter.value;

        switch (filter.operator) {
          case "equals":
            return String(value) === String(filterValue);
          case "contains":
            return String(value).toLowerCase().includes(String(filterValue).toLowerCase());
          case "isEmpty":
            return !value;
          case "isNotEmpty":
            return !!value;
          default:
            return true;
        }
      });
    });

    if (sorts.length > 0) {
      filtered.sort((a, b) => {
        for (const sort of sorts) {
          const aVal = a.data?.[sort.field];
          const bVal = b.data?.[sort.field];
          if (aVal === bVal) continue;
          const comparison = aVal < bVal ? -1 : 1;
          return sort.direction === "asc" ? comparison : -comparison;
        }
        return 0;
      });
    }

    setFilteredRecords(filtered);
  };

  const createNewRecord = async () => {
    try {
      const { data, error } = await supabase
        .from("records")
        .insert([{
          table_id: table.id,
          data: {},
          created_by: user?.id,
        }])
        .select()
        .single();

      if (error) throw error;
      setRecords([data, ...records]);
      setSelectedRecord(data);
      setEditingRecord(true);
    } catch (error: any) {
      toast.error("Failed to create record");
    }
  };

  const deleteRecord = async (recordId: string) => {
    try {
      const { error } = await supabase
        .from("records")
        .delete()
        .eq("id", recordId);

      if (error) throw error;
      setRecords(records.filter((r) => r.id !== recordId));
      toast.success("Record deleted");
    } catch (error: any) {
      toast.error("Failed to delete record");
    }
  };

  const primaryField = fields.find(f => f.type === "text") || fields[0];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search and filters */}
      <div className="p-3 space-y-3 border-b bg-background sticky top-0 z-10">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search records..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 h-10"
          />
        </div>

        <div className="flex items-center gap-2">
          <Sheet open={showFilters} onOpenChange={setShowFilters}>
            <SheetTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5 flex-1">
                <SlidersHorizontal className="h-4 w-4" />
                Filters
                {filters.length > 0 && (
                  <span className="ml-1 text-xs bg-primary/20 px-1.5 rounded-full">
                    {filters.length}
                  </span>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent side="bottom" className="h-[70vh]">
              <SheetHeader>
                <SheetTitle>Filters & Sorting</SheetTitle>
              </SheetHeader>
              <div className="py-4 space-y-6">
                <div>
                  <h4 className="text-sm font-medium mb-2">Filters</h4>
                  <FilterBuilder
                    fields={fields}
                    currentFilters={filters}
                    onApplyFilters={(newFilters) => {
                      setFilters(newFilters);
                    }}
                  />
                </div>
                <div>
                  <h4 className="text-sm font-medium mb-2">Sorting</h4>
                  <SortBuilder
                    fields={fields}
                    currentSort={sorts}
                    onApplySort={(newSorts) => {
                      setSorts(newSorts);
                    }}
                  />
                </div>
              </div>
            </SheetContent>
          </Sheet>

          <Button
            size="sm"
            onClick={createNewRecord}
            className="gap-1.5 flex-1"
          >
            <Plus className="h-4 w-4" />
            New Record
          </Button>
        </div>
      </div>

      {/* Records list */}
      <ScrollArea className="flex-1">
        <div className="p-3 space-y-2 pb-20">
          {filteredRecords.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              {records.length === 0 ? (
                <div>
                  <p className="mb-2">No records yet</p>
                  <Button variant="outline" size="sm" onClick={createNewRecord}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add your first record
                  </Button>
                </div>
              ) : (
                "No records match your filters"
              )}
            </div>
          ) : (
            filteredRecords.map((record) => (
              <MobileRecordCard
                key={record.id}
                record={record}
                fields={fields}
                primaryField={primaryField}
                onEdit={() => {
                  setSelectedRecord(record);
                  setEditingRecord(true);
                }}
                onDelete={() => deleteRecord(record.id)}
              />
            ))
          )}
        </div>
      </ScrollArea>

      {/* Record Editor */}
      {selectedRecord && (
        <RecordEditor
          record={selectedRecord}
          fields={fields}
          open={editingRecord}
          workspaceId={workspaceId}
          onClose={() => {
            setEditingRecord(false);
            setSelectedRecord(null);
          }}
          onSave={() => {
            fetchData();
            setEditingRecord(false);
            setSelectedRecord(null);
          }}
        />
      )}
    </div>
  );
};
